import { computed, ref, watch, type ComputedRef, type Ref } from "vue";

import type { EntityChange } from "@/pages/games/GameDisplay/children/versionHistory/versionDiff.types";

export default function useEntitySelection(
  allChanges: Ref<EntityChange[]> | ComputedRef<EntityChange[]>,
  lockedIds: Ref<Set<string>> | ComputedRef<Set<string>> = computed(() => new Set<string>())
) {
  const manuallySelectedIds = ref<Set<string>>(new Set());

  // Seed manuallySelectedIds with locked IDs when they become available
  watch(
    lockedIds,
    (ids) => {
      if (ids.size > 0) {
        const next = new Set(manuallySelectedIds.value);
        ids.forEach((id) => next.add(id));
        manuallySelectedIds.value = next;
      }
    },
    { immediate: true }
  );

  const changeMap = computed(() => new Map(allChanges.value.map((c) => [c.entityId, c])));

  const selectedIds = computed<Set<string>>(() => {
    const result = new Set<string>();
    const queue = [...manuallySelectedIds.value];

    while (queue.length > 0) {
      const id = queue.shift()!;
      if (result.has(id)) {
        continue;
      }
      result.add(id);

      const change = changeMap.value.get(id);
      if (!change) {
        continue;
      }

      for (const deps of Object.values(change.dependenciesWithChangesByType)) {
        for (const dep of deps) {
          if (!result.has(dep.id)) {
            queue.push(dep.id);
          }
        }
      }
    }

    return result;
  });

  const toggleEntity = (entityId: string) => {
    const isLocked = lockedIds.value.has(entityId);
    const isDependencyOnly = selectedIds.value.has(entityId) && !manuallySelectedIds.value.has(entityId);
    if (isLocked || isDependencyOnly) {
      return;
    }

    const next = new Set(manuallySelectedIds.value);
    if (next.has(entityId)) {
      next.delete(entityId);
    } else {
      next.add(entityId);
    }
    manuallySelectedIds.value = next;
  };

  const clearSelection = () => {
    // Keep locked IDs when clearing
    manuallySelectedIds.value = new Set(lockedIds.value);
  };

  const selectedChanges = computed(() => allChanges.value.filter((c) => selectedIds.value.has(c.entityId)));

  return {
    selectedIds,
    manuallySelectedIds,
    toggleEntity,
    clearSelection,
    selectedChanges,
  };
}
